import React from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { FaArrowLeft, FaGithub } from 'react-icons/fa';
import { profile } from '../data/portfolio';
import { useI18n } from '../context/LanguageContext';

const float = keyframes`
  0%, 100% { transform: translateY(0) rotate(-2deg); }
  50% { transform: translateY(-16px) rotate(2deg); }
`;
const drift = keyframes`
  0% { transform: translate(0,0) scale(1); }
  50% { transform: translate(4%,-4%) scale(1.1); }
  100% { transform: translate(0,0) scale(1); }
`;
const glitch = keyframes`
  0%, 100% { clip-path: inset(0 0 0 0); transform: translate(0); }
  20% { clip-path: inset(20% 0 40% 0); transform: translate(-4px, 2px); }
  40% { clip-path: inset(60% 0 10% 0); transform: translate(4px, -2px); }
  60% { clip-path: inset(10% 0 70% 0); transform: translate(-3px, 1px); }
  80% { clip-path: inset(45% 0 25% 0); transform: translate(3px, -1px); }
`;

const Wrap = styled.div`
  position: relative;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 2rem;
  gap: 1rem;
  overflow: hidden;
`;

const Mesh = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  span {
    position: absolute;
    border-radius: 50%;
    filter: blur(90px);
    opacity: var(--mesh-op);
  }
  span:nth-child(1) { width: 420px; height: 420px; top: 6%; left: 8%;
    background: radial-gradient(circle, #6366f1, transparent 70%); animation: ${drift} 16s ease-in-out infinite; }
  span:nth-child(2) { width: 380px; height: 380px; bottom: 8%; right: 10%;
    background: radial-gradient(circle, #ec4899, transparent 70%); animation: ${drift} 20s ease-in-out infinite; }
`;

const Big = styled(motion.h1)`
  position: relative;
  font-family: var(--font-heading);
  font-size: clamp(6rem, 22vw, 13rem);
  line-height: 1;
  letter-spacing: -0.04em;
  z-index: 1;
  animation: ${float} 6s ease-in-out infinite;
  background: var(--gradient-text);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;

  /* glitch echoes */
  &::before, &::after {
    content: '404';
    position: absolute;
    inset: 0;
    background: var(--gradient-text);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    opacity: 0.5;
  }
  &::before { animation: ${glitch} 3.6s infinite steps(2); mix-blend-mode: screen; }
  &::after { animation: ${glitch} 2.8s infinite reverse steps(2); mix-blend-mode: screen; }
`;

const Sub = styled.h2` z-index: 1; font-size: 1.6rem; `;
const Text = styled.p` z-index: 1; color: var(--text-muted); max-width: 440px; margin-bottom: 1rem; `;

const Actions = styled.div`
  z-index: 1; display: flex; gap: 0.85rem; flex-wrap: wrap; justify-content: center;
`;

const Primary = styled.button`
  display: inline-flex; align-items: center; gap: 0.55rem;
  padding: 0.9rem 1.6rem; border: none; border-radius: 999px;
  font-weight: 600; color: #fff; cursor: pointer;
  background: var(--gradient-accent);
  box-shadow: 0 12px 30px -12px rgba(99, 102, 241, 0.7);
  transition: transform 0.25s ease;
  &:hover { transform: translateY(-3px); }
`;

const Ghost = styled.a`
  display: inline-flex; align-items: center; gap: 0.55rem;
  padding: 0.9rem 1.5rem; border-radius: 999px;
  border: 1px solid var(--border-strong); background: var(--surface);
  color: var(--text); font-weight: 600; cursor: pointer;
  transition: border-color 0.25s ease, background 0.25s ease;
  &:hover { border-color: var(--primary-light); background: var(--surface-hover); }
`;

const Hint = styled.p`
  z-index: 1; margin-top: 1.5rem; font-size: 0.8rem; color: var(--text-faint);
  code { color: var(--primary-light); font-family: var(--font-mono, monospace); }
`;

const NotFound = () => {
  const { t } = useI18n();

  return (
  <Wrap>
    <Mesh><span /><span /></Mesh>
    <Big
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
    >
      404
    </Big>
    <Sub>{t.notFound.sub}</Sub>
    <Text>{t.notFound.text}</Text>
    <Actions>
      <Primary onClick={() => (window.location.href = '/')}>
        <FaArrowLeft /> {t.notFound.home}
      </Primary>
      <Ghost href={profile.github} target="_blank" rel="noopener noreferrer">
        <FaGithub /> {t.notFound.github}
      </Ghost>
    </Actions>
    <Hint>{t.notFound.hintA} <code>↑ ↑ ↓ ↓ ← → ← → B A</code> {t.notFound.hintB}</Hint>
  </Wrap>
  );
};

export default NotFound;
